import React from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { timeConversion } from "client-app-core";
import moment from "moment";

import Cancel from "@mui/icons-material/Cancel";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";

import Bell from "orion-components/CBComponents/Icons/Bell";
import { Collection } from "orion-components/CBComponents";
import { Translate } from "orion-components/i18n";
import { deleteReminder } from "../reminders/actions";
import { closeNotification } from "../notifications/actions";

const propTypes = {
	dir: PropTypes.string,
	item: PropTypes.object,
	dismissButton: PropTypes.bool,
	dismissNotification: PropTypes.func,
	editMode: PropTypes.bool,
	editReminder: PropTypes.func,
	selectedReminders: PropTypes.array,
	setSelectedReminders: PropTypes.func,
	customStyles: PropTypes.object
};

const defaultProps = {
	item: {},
	editReminder: () => {},
	setSelectedReminders: () => {},
	customStyles: {},
	dismissNotification: () => {}
};

const Card = ({
	dir,
	item,
	dismissButton,
	dismissNotification,
	editMode,
	editReminder,
	selectedReminders,
	setSelectedReminders,
	customStyles
}) => {
	const dispatch = useDispatch();

	const { id, appId, name, subject, description, body, dateTime, createdDate, title, dueDate, source } = item;

	const user = useSelector(state => state.session.user.profile);
	const timeFormatPreference = useSelector(state => state.appState.global.timeFormat);
	const locale = useSelector(state => state.i18n.locale);
	const convertedDateTime = timeConversion.convertToUserTime(
		moment(dateTime || dueDate || createdDate).locale(locale),
		`full_${timeFormatPreference}`,
		locale
	);

	const styles = {
		iconButton: {
			color: "#fff",
			padding: "unset"
		},
		cancel: {
			fontSize: 20
		},
		iconStyles: {
			backgroundSize: "contain",
			top: "-10px",
			left: "-4px",
			height: "34px",
			width: "34px"
		},
		checkbox: {
			color: "#B7BABF",
			...(dir === "rtl" ? { paddingRight: 0 } : { paddingLeft: 0 }),
			"&.Mui-checked": {
				color: "#1688bd!important"
			}
		},
		bell: {
			width: 26,
			height: 26,
			background: "#979798",
			padding: 5,
			marginRight: 10,
			borderRadius: "5px",
			display: "flex",
			alignItems: "center",
			justifyContent: "center"
		},
		retry: {
			fontSize: 10,
			display: "flex",
			alignItems: "center",
			...(dir === "rtl" ? { marginLeft: 10 } : { marginRight: 10 })
		},
		secondaryDesc: {
			maxHeight: "30px"
		}
	};

	const generateIcon = () => {
		// This is temporary logic for displaying app icons and is subject to change once the notification framework is finalized.
		if (source?.type === "activity" && appId) {
			const appIconSrc = `/_fileDownload?bucketName=app-icons&fileName=app.${appId.replace(/-app/g, "")}.png`;

			return (
				<span
					style={{
						...styles.iconStyles,
						...{
							backgroundImage: `url(${appIconSrc})`
						}
					}}
					className={"widget-icon"}
				/>
			);
		} else if (source?.type === "reminder" || id) {
			// Reminder source type indicates that the item is a reminder notification.Using 'id' to identify if the item is an actual reminder and not an invalid object.
			return (
				<div style={styles.bell}>
					<Bell customStyle={{ width: 12, height: 14 }} />
				</div>
			);
		} else {
			return <></>;
		}
	};

	const handleDelete = e => {
		e.stopPropagation();
		source ? dispatch(closeNotification(id)) : dispatch(deleteReminder(id));
	};

	const endAdornment = dismissButton ? (
		<a
			onClick={dismissNotification}
			className="cb-font-b2 cb-font-link"
			style={{ fontSize: 10, display: "flex", alignItems: "center" }} data-testid="orion-components-dock-notifications-center-components-card-a"
		>
			<Translate value="global.dock.notificationsCenter.dismiss" />
		</a>
	) : (
		<IconButton style={styles.iconButton} onClick={handleDelete} data-testid="orion-components-dock-notifications-center-components-card-icon-button">
			<Cancel style={styles.cancel} />
		</IconButton>
	);

	const secondaryText = (
		<>
			<div style={styles.secondaryDesc}>{description || body}</div>
			<div style={{ marginTop: 4 }}>{convertedDateTime}</div>
		</>
	);

	const handleSelect = () => {
		if (editMode) editReminder(item);
	};

	const selectReminder = e => {
		const checked = e.target.checked;
		setSelectedReminders(prev => (checked ? [...prev, id] : prev.filter(reminderId => reminderId !== id)));
	};

	return id ? (
		<div style={{ display: "flex", width: "100%", ...customStyles }}>
			{editMode && (
				<Checkbox
					checked={selectedReminders?.includes(id)}
					onChange={selectReminder}
					color="primary"
					sx={styles.checkbox} data-testid="orion-components-dock-notifications-center-components-card-checkbox"
				/>
			)}
			<Collection
				key={id}
				id={id}
				primaryText={name || title || subject}
				secondaryText={secondaryText}
				secondaryStyles={{ lineHeight: "10px", marginTop: 4 }}
				icon={generateIcon()}
				handleSelect={handleSelect}
				dir={dir}
				endAdornment={endAdornment}
				minimized={true}
				customStyles={{ width: "100%", height: "100%" }}
				cardStyles={{ height: 80, padding: "8px 15px!important" }}
				iconStyles={{ minWidth: 42 }}
			/>
		</div>
	) : null;
};

Card.propTypes = propTypes;
Card.defaultProps = defaultProps;

export default Card;
